import { useTranslation } from 'react-i18next'

import { SmsFindingSeverity } from '#/shared/api/generated/communication/models'
import type {
  SmsBlockerRead,
  SmsFindingRead,
} from '#/shared/api/generated/communication/models'
import { cn } from '#/shared/lib/utils'
import { AlertTriangleIcon, BanIcon } from '#/shared/ui/icons'

interface Row {
  code: string
  severity: SmsFindingSeverity
  message: string
}

const ROW_CLASS: Record<SmsFindingSeverity, string> = {
  [SmsFindingSeverity.block]: 'text-red-500',
  [SmsFindingSeverity.warn]: 'text-orange-700',
  [SmsFindingSeverity.info]: 'text-warm-500',
}

const SEVERITY_LABEL_KEY: Record<SmsFindingSeverity, string> = {
  [SmsFindingSeverity.block]: 'smsDeliverability.severity.block',
  [SmsFindingSeverity.warn]: 'smsDeliverability.severity.warn',
  [SmsFindingSeverity.info]: 'smsDeliverability.severity.info',
}

/** A blocker is always fatal; a finding carries its own severity. */
function toRows(blockers: SmsBlockerRead[], findings: SmsFindingRead[]): Row[] {
  return [
    ...blockers.map((blocker) => ({
      code: blocker.code,
      severity: SmsFindingSeverity.block,
      message: blocker.message,
    })),
    ...findings.map((finding) => ({
      code: finding.code,
      severity: finding.severity,
      message: finding.message,
    })),
  ]
}

function SeverityIcon({ severity }: { severity: SmsFindingSeverity }) {
  const className = 'mt-0.25 size-3.5 shrink-0'
  if (severity === SmsFindingSeverity.block) {
    return <BanIcon className={className} />
  }
  if (severity === SmsFindingSeverity.warn) {
    return <AlertTriangleIcon className={className} />
  }
  return null
}

/**
 * What the server's pre-send check found on this draft: the hard `blockers`
 * that stop the send outright (no consent, sender can't text, quota spent) and
 * the content `findings` that make a carrier likely to spam-block it (fancy
 * font, spam-trigger words, link shorteners, ALL CAPS).
 *
 * The message text is the server's own — it names the offending words, which
 * the client has no list of. Findings are advisory: the composer still sends,
 * and even a clean message can come back `40002`. Blockers are not, and the
 * composers that render them disable Send while one is present.
 */
export function SmsDeliverabilityNotice({
  findings = [],
  blockers = [],
  className,
}: {
  findings?: SmsFindingRead[]
  blockers?: SmsBlockerRead[]
  className?: string
}) {
  const { t } = useTranslation()
  const rows = toRows(blockers, findings)
  if (rows.length === 0) return null
  return (
    <ul className={cn('flex flex-col gap-1 text-xs', className)}>
      {rows.map((row) => (
        <li
          key={`${row.severity}-${row.code}`}
          className={cn('flex items-start gap-1.25', ROW_CLASS[row.severity])}
        >
          <SeverityIcon severity={row.severity} />
          <span className="sr-only">{t(SEVERITY_LABEL_KEY[row.severity])}</span>
          <span className="min-w-0">{row.message}</span>
        </li>
      ))}
    </ul>
  )
}
